<template>
  <div class="home-hero">
    <swiper
        v-if="items"
        slides-per-view="auto"
        ref="swiperElRef"
        class="home-hero__slider home-hero__slider--home-hero"
        @swiper="useSwiper"
        :navigation="true"
        :pagination="true"
        :loop="true"
        :modules="modules"
        @autoplayTimeLeft="onAutoplayTimeLeft"
        @init="onInit"
        :autoplay="{
          delay: 10000,
          disableOnInteraction: false,
        }"
    >
      <swiper-slide
          v-for="(item, index) in items"
          :key="`hero_item_${item.id}`"
      >
        <AppHomeHeroItem :data="item" :index="index"/>
      </swiper-slide>
    </swiper>
  </div>

</template>

<script lang="ts" setup>
import {ref} from "vue";
import {TCmsHeroItem} from "~/types/_cms-types";
import {Swiper, SwiperSlide} from 'swiper/vue'
import 'swiper/css'
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import {Autoplay, Navigation, Pagination} from "swiper/modules";

const props = defineProps<{
  items: Array<TCmsHeroItem>,
}>()

const modules = [Autoplay, Navigation, Pagination]
const swiperElRef = ref<HTMLElement>()
let timeoutHandler: Timeout = null;

const onAutoplayTimeLeft = (swiper, time, progress) => {

  const callback = () => {
    if (swiperElRef.value && swiperElRef.value.$el) {
      const activeBullet = swiperElRef.value.$el.querySelector('.swiper-pagination-bullet-active')
      const first = swiper.realIndex === 0

      if (activeBullet) {
        activeBullet.style.setProperty('--progress', `${(1 - progress) * 100}%`)
      }

      if (first) {
        swiper.pagination.bullets.forEach(
            bullet => bullet.classList.remove('swiper-pagination-bullet-past')
        )
      } else {
        if (swiper.pagination.bullets[swiper.realIndex]) {
          swiper.pagination.bullets[swiper.realIndex].classList.remove('swiper-pagination-bullet-past')
          for (let bulletIndex = 0; bulletIndex < swiper.realIndex; bulletIndex++) {
            swiper.pagination.bullets[bulletIndex].classList.add('swiper-pagination-bullet-past')
          }
        }
      }
    }
  }

  // throttle the update amount to 15fps
  if (timeoutHandler === null) {
    timeoutHandler = setTimeout(function () {
      callback();
      clearInterval(timeoutHandler);
      timeoutHandler = null;
    }, 1000 / 15);
  }
}

const onInit = (swiper) => {
  nextTick(() => {
    swiper.pagination.el.classList.add('pagination-active')
  })
}

</script>

<style scoped lang="postcss">
.home-hero {
  position: relative;
  background: rgba(0,0,0,0.25);
  overflow: hidden;

  &__slider {
    @mixin app-show-hero;

    &--home-hero {
      --swiper-pagination-bullet-height: 3px;
      --swiper-theme-color: #00ffe0;
      --swiper-pagination-bullet-inactive-opacity: 1;
      --swiper-pagination-bullet-inactive-color: #fff;
      --swiper-pagination-bullet-horizontal-gap: 5px;
      --swiper-pagination-bullet-border-radius: 2px;
      :deep(.swiper-pagination) {
        position: absolute;
        bottom: 0px;
        left: 0px;
        display: flex;
        width: 100%;
        padding-left: 0.75rem;
        padding-right: 0.75rem;
        padding-bottom: 1.25rem;
        opacity: 0;
        transition: opacity 0.5s ease;

        @media (--sm) {
          padding-left: 1rem;
          padding-right: 1rem;

          max-width: var(--screen-width-sm);
          left: calc((100vw - var(--screen-width-sm)) / 2);
        }

        @media (--md) {
          padding-left: 1.25rem;
          padding-right: 1.25rem;
          padding-bottom: 2rem;

          max-width: var(--screen-width-md);
          left: calc((100vw - var(--screen-width-md)) / 2);
        }

        @media (--lg) {
          padding-left: 1.5rem;
          padding-right: 1.5rem;

          max-width: var(--screen-width-lg);
          left: calc((100vw - var(--screen-width-lg)) / 2);
        }

        @media (--xl) {
          padding-left: 116px;
          padding-right: 116px;
          padding-bottom: 2.5rem;

          max-width: var(--screen-width-xl);
          left: calc((100vw - var(--screen-width-xl)) / 2);
        }

        @media (--xxl) {
          padding-left: 140px;
          padding-right: 140px;

          max-width: var(--screen-width-xxl);
          left: calc((100vw - var(--screen-width-xxl)) / 2);
        }
      }

      :deep(.pagination-active) {
        opacity: 1;
      }

      :deep(.swiper-pagination-bullet) {
        position: relative;
        height: 0.25rem;
        flex: 1 1 auto;

        &::after {
          position: absolute;
          left: 0px;
          top: 0px;
          height: 100%;
          content: "";
          border-radius: var(--swiper-pagination-bullet-border-radius);
          overflow: hidden;
          background: var(--swiper-theme-color);
        }

        &.swiper-pagination-bullet-active {
          background: var(--swiper-pagination-bullet-inactive-color);

          &::after {
            position: absolute;
            left: 0px;
            top: 0px;
            height: 100%;
            content: "";
            background: var(--swiper-theme-color);
            width: var(--progress);
          }
        }

        &.swiper-pagination-bullet-past {
          &::after {
            width: 100%;
          }
        }
      }

      :deep(.swiper-button-prev),
      :deep(.swiper-button-next) {
        top: 0px;
        margin-top: 0px;
        display: none;
        width: 20%;
        opacity: 0;
        height: calc(100% - 120px);

        @media (--md) {
          display: block;
        }
      }

      :deep(.swiper-button-prev) {
        left: 0px;
      }

      :deep(.swiper-button-next) {
        right: 0px;
      }

      :deep(.swiper-slide) {
        .home-hero-item__content {
          opacity: 0;
          transform-origin: 20% 80%;
          transform: scale(0.7);
          transition: opacity 500ms 500ms ease, transform 500ms 350ms ease;
        }
      }

      :deep(.swiper-slide-active) {
        .home-hero-item__content {
          opacity: 1 !important;
          transform: scale(1);
        }
      }
    }
  }
}
</style>
